<template>
    <div class="dashboard-container">
        <Sidebar @refresh="cargarDatos" />
        
        <main class="main-content">
            <header class="header-dashboard">
                <div>
                    <h1>Calendario de Vencimientos</h1>
                    <p>Pólizas con vencimiento en los próximos meses</p>
                </div>
                <div class="user-profile">
                    <div class="avatar-circle">{{ userInitials }}</div>
                    <div>
                        <p class="user-name">{{ userEmail }}</p>
                        <p class="user-role">Cliente</p>
                    </div>
                </div>
            </header>

            <section class="calendar-container">
                <div class="calendar-header">
                    <h3>Próximos Vencimientos (Preaviso 2 meses)</h3>
                    <div class="filter-tabs">
                        <button :class="['filter-tab', { active: filtro === 'todos' }]" @click="filtro = 'todos'">
                            Todos
                        </button>
                        <button :class="['filter-tab', { active: filtro === 'inminente' }]" @click="filtro = 'inminente'">
                            <span class="danger-dot"></span> Inminentes (&le;30 días)
                        </button>
                        <button :class="['filter-tab', { active: filtro === 'alerta' }]" @click="filtro = 'alerta'">
                            <span class="warning-dot"></span> Alerta (≤60 días)
                        </button>
                    </div>
                </div>

                <div v-if="loading" class="loading-state">
                    <p>Cargando vencimientos...</p>
                </div>

                <div v-else-if="polizasFiltradas.length === 0" class="empty-state">
                    <p>No hay pólizas con vencimiento próximo.</p>
                </div>

                <div v-else class="calendar-grid">
                    <div v-for="poliza in polizasFiltradas" :key="poliza.id" 
                         :class="['maturity-card', getCardClass(poliza.diasRestantes)]">
                        <div class="maturity-header">
                            <span class="policy-tag">{{ poliza.numero_poliza || 'Pendiente' }}</span>
                            <span :class="['status-pill', getCardClass(poliza.diasRestantes)]">
                                {{ getStatusLabel(poliza.diasRestantes) }}
                            </span>
                        </div>
                        <div class="maturity-date">{{ formatDate(poliza.fecha_vencimiento) }}</div>
                        <div class="maturity-info">
                            <p><strong>Producto:</strong> {{ poliza.producto }}</p>
                            <p><strong>Titular:</strong> {{ poliza.titular }}</p>
                            <p><strong>Días restantes:</strong> {{ poliza.diasRestantes }} días</p>
                        </div>
                        <div class="maturity-actions">
                            <a href="#" class="btn-action-sm btn-renew" @click.prevent="renovarPoliza(poliza)">
                                Renovar Ahora
                            </a>
                            <router-link :to="{ name: 'AreaPrivada' }" class="btn-action-sm btn-detail">
                                Gestionar
                            </router-link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    </div>
</template>

<script>
import Sidebar from '@/components/layout/Sidebar.vue'
import { API_URL } from '@/config'

export default {
    name: 'Vencimientos',
    components: { Sidebar },
    data() {
        return {
            loading: true,
            userEmail: '',
            userHash: '',
            polizas: [],
            filtro: 'todos',  // todos, inminente, alerta
            renovando: null  // ID de la póliza que se está renovando
        }
    },
    computed: {
        userInitials() {
            return this.userEmail ? this.userEmail.substring(0, 2).toUpperCase() : 'CL'
        },
        polizasFiltradas() {
            if (this.filtro === 'todos') {
                return this.polizas
            } else if (this.filtro === 'inminente') {
                return this.polizas.filter(p => p.diasRestantes <= 30 && p.diasRestantes > 0)
            } else if (this.filtro === 'alerta') {
                return this.polizas.filter(p => p.diasRestantes <= 60 && p.diasRestantes > 30)
            }
            return this.polizas
        }
    },
    mounted() {
        this.verificarSesion()
    },
    methods: {
        verificarSesion() {
            const hash = localStorage.getItem('expat_visa_session')
            const user = localStorage.getItem('user')
            
            if (!hash || !user) {
                this.$router.push({ name: 'login' })
                return
            }
            
            this.userEmail = user
            this.userHash = hash
            this.cargarDatos()
        },
        
        async cargarDatos() {
            this.loading = true
            try {
                const response = await fetch(`${API_URL}/com.api.get-polizas.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.VUE_APP_TOKEN}`
                    },
                    body: JSON.stringify({ hash: this.userHash })
                })
                
                const data = await response.json()
                
                if (data.success && data.polizas) {
                    // Calcular fecha de vencimiento (1 año después de la contratación)
                    this.polizas = data.polizas.map(p => {
                        const fechaContratacion = new Date(p.fecha)
                        const fechaVencimiento = new Date(fechaContratacion)
                        fechaVencimiento.setFullYear(fechaVencimiento.getFullYear() + 1)
                        
                        const hoy = new Date()
                        const diffTime = fechaVencimiento - hoy
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                        
                        return {
                            ...p,
                            fecha_vencimiento: fechaVencimiento.toISOString().split('T')[0],
                            diasRestantes: diffDays
                        }
                    })
                    
                    // Ordenar por días restantes (menor a mayor)
                    this.polizas.sort((a, b) => a.diasRestantes - b.diasRestantes)
                }
            } catch (error) {
                console.error('Error:', error)
            } finally {
                this.loading = false
            }
        },
        
        getCardClass(dias) {
            if (dias <= 30 && dias > 0) return 'immediate'
            if (dias <= 60 && dias > 30) return 'soon'
            return ''
        },
        
        getStatusLabel(dias) {
            if (dias <= 30 && dias > 0) return 'VENCIMIENTO INMINENTE'
            if (dias <= 60 && dias > 30) return 'ALERTA: 2 MESES PARA VENCIMIENTO'
            return ''
        },
        
        formatDate(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            return date.toLocaleDateString('es-ES', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
            })
        },
        
        renovarPoliza(poliza) {
            // Redirigir a contratación con datos de la póliza
            this.$router.push({ 
                name: 'contratacion',
                query: {
                    renovar: poliza.id,
                    producto: poliza.producto
                }
            })
        },
        
        cerrarSesion() {
            localStorage.removeItem('expat_visa_session')
            localStorage.removeItem('user')
            localStorage.removeItem('user_name')
            localStorage.removeItem('user_rol')
            this.$router.push({ name: 'login' })
        },
        async renovarPoliza(poliza) {
            // Confirmar renovación
            const confirmado = confirm(
                `¿Renovar la póliza ${poliza.numero_poliza}?\n\n` +
                `Producto: ${poliza.producto}\n` +
                `Titular: ${poliza.titular}\n\n` +
                `La nueva fecha de vencimiento será ${this.calcularNuevaFechaVencimiento(poliza.fecha)}`
            )
            
            if (!confirmado) return
            
            this.renovando = poliza.id
            
            try {
                const response = await fetch(`${API_URL}/com.api.renovar-poliza.php`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.VUE_APP_TOKEN}`
                    },
                    body: JSON.stringify({
                        poliza_id: poliza.id,
                        hash: this.userHash
                    })
                })
                
                const result = await response.json()
                
                if (result.success) {
                    alert(`✅ Póliza ${poliza.numero_poliza} renovada correctamente.\n\nNueva fecha de vencimiento: ${result.nueva_fecha}`)
                    // Recargar datos
                    this.cargarDatos()
                } else {
                    throw new Error(result.error || 'Error al renovar')
                }
                
            } catch (error) {
                console.error('Error:', error)
                alert('❌ Error al renovar la póliza: ' + error.message)
            } finally {
                this.renovando = null
            }
        },
        calcularNuevaFechaVencimiento(fechaContratacionActual) {
            const fecha = new Date(fechaContratacionActual)
            fecha.setFullYear(fecha.getFullYear() + 1)
            return fecha.toLocaleDateString('es-ES')
        },
        gestionarPoliza(poliza) {
            // Guardar en sessionStorage que viene del calendario con filtro
            sessionStorage.setItem('poliza_seleccionada', JSON.stringify({
                id: poliza.id,
                numero_poliza: poliza.numero_poliza
            }))
            
            // Redirigir al área privada con parámetro
            this.$router.push({ 
                name: 'AreaPrivada',
                query: { poliza: poliza.id, desde: 'vencimientos' }
            })
        }
    }
}
</script>

<style scoped>
.dashboard-container {
    display: flex;
    min-height: 100vh;
    background: #f5f7fa;
    font-family: 'Open Sans', sans-serif;
}

.main-content {
    margin-left: 260px;
    flex-grow: 1;
    padding: 40px;
}

.header-dashboard {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
}

.header-dashboard h1 {
    font-size: 1.8rem;
    color: #202124;
    margin-bottom: 5px;
}

.header-dashboard p {
    color: #5f6368;
}

.user-profile {
    display: flex;
    align-items: center;
    gap: 12px;
}

.avatar-circle {
    width: 40px;
    height: 40px;
    background: #1C4A8D;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    text-transform: uppercase;
}

.user-name {
    font-weight: 600;
    font-size: 0.9rem;
    margin: 0;
}

.user-role {
    font-size: 0.75rem;
    color: #5f6368;
    margin: 0;
}

/* Calendar Container */
.calendar-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    padding: 30px;
}

.calendar-header {
    margin-bottom: 25px;
    border-bottom: 1px solid #f1f3f4;
    padding-bottom: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;
}

.calendar-header h3 {
    font-size: 1.1rem;
    color: #202124;
    margin: 0;
}

.filter-tabs {
    display: flex;
    gap: 10px;
}

.filter-tab {
    padding: 6px 14px;
    background: #f1f3f4;
    border: none;
    border-radius: 20px;
    cursor: pointer;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 6px;
}

.filter-tab.active {
    background: #1C4A8D;
    color: white;
}

.danger-dot {
    width: 8px;
    height: 8px;
    background: #d93025;
    border-radius: 50%;
    display: inline-block;
}

.warning-dot {
    width: 8px;
    height: 8px;
    background: #ef6c00;
    border-radius: 50%;
    display: inline-block;
}

/* Calendar Grid */
.calendar-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
}

.maturity-card {
    border: 1px solid #e1e4e8;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: all 0.2s;
    background: white;
}

.maturity-card.soon {
    border-color: #ef6c00;
    background: #fff4e5;
}

.maturity-card.immediate {
    border-color: #d93025;
    background: #fce8e6;
}

.maturity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.policy-tag {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    color: #5f6368;
}

.status-pill {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 700;
}

.status-pill.soon {
    background: #ffe0b2;
    color: #ef6c00;
}

.status-pill.immediate {
    background: #ffcdd2;
    color: #d32f2f;
}

.maturity-date {
    font-size: 1.2rem;
    font-weight: 700;
    color: #202124;
}

.maturity-info {
    font-size: 0.9rem;
    color: #5f6368;
}

.maturity-info strong {
    color: #202124;
}

.maturity-actions {
    margin-top: auto;
    padding-top: 15px;
    display: flex;
    gap: 10px;
}

.btn-action-sm {
    padding: 8px 12px;
    font-size: 0.8rem;
    border-radius: 6px;
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;
    text-align: center;
    flex: 1;
    display: inline-block;
}

.btn-renew {
    background: #1C4A8D;
    color: white;
    border: none;
}

.btn-renew:hover {
    background: #153a70;
}

.btn-detail {
    background: white;
    color: #1C4A8D;
    border: 1px solid #1C4A8D;
}

.btn-detail:hover {
    background: #f1f3f9;
}

.loading-state, .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #5f6368;
}

@media (max-width: 768px) {
    .main-content {
        margin-left: 80px;
        padding: 20px;
    }
    .calendar-header {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>